import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */

/* @jsx mdx */

export const _frontmatter = {
  "title": "Javascript's lack of strftime",
  "description": "",
  "published": "2011-12-12T00:00:00.000Z",
  "redirect_from": ["/blog/javascripts-lack-of-strftime/swizec/3164"],
  "categories": "ISO 8601, JavaScript, Languages, Programming, Tools, Uncategorized",
  "hero": "../../../defaultHero.jpg"
};
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">
    <p>{`You know that one piece of shitty code that always makes you cringe? Something along the lines of `}<em parentName="p">{`months = `}{`[`}{`'Jan', 'Feb' ....]; dateString = date.day()+' '+months`}{`[`}{`date.month()];`}</em></p>
    <p><img parentName="p" {...{
        "src": "https://swizec.com/blog/wp-content/uploads/2011/12/300px-Nice_air-raid_siren2.jpg",
        "alt": "An air-raid siren in Nice, France, is still op...",
        "title": "An air-raid siren in Nice, France, is still op..."
      }}></img></p>
    <p>{`Yeah `}<strong parentName="p">{`that`}</strong>{` piece of code. Let's talk about `}<em parentName="p">{`that`}</em>{`.`}</p>
    <p>{`It sucks. There is a special circle of hell for people who do it and yet JavaScript developers are forced to doing it all the freaking time!`}</p>
    <p>{`In `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/JavaScript",
        "title": "JavaScript"
      }}>{`JavaScript`}</a>{` that's the only way you can do it. I shit you not.`}</p>
    <p>{`A couple of days ago I caught myself writing code like that and red flags and alarms and air-raid sirens started going off in my head. I felt like that proverbial axe murderer reading my code who knows where I live was already breathing down my neck.`}</p>
    <p>{`Scary situation that.`}</p>
    <p>{`But when I went looking for an elegant way of coercing datetime into a `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/String_%28computer_science%29",
        "title": "String (computer science)"
      }}>{`string`}</a>{`, I found the whole situation rather lacking. Turns out JavaScript doesn't have a native `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/Time.h",
        "title": "Time.h"
      }}>{`strftime`}</a>{` function even though it is surprisingly brilliant in converting strings to native representations.`}</p>
    <h2 {...{
      "id": "string---date"
    }}>{`String -> Date`}</h2>
    <p>{`Have a string? Want a date? JavaScript's got your back bro!`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`// all of this correctly returns a millisecond timestamp since unix epoch
// the string argument can also be passed to the date constructor by the way (returns a correct Date object)
Date.parse("Aug 9, 1995");
Date.parse("Wed, 09 Aug 1995 00:00:00 GMT");
Date.parse("Wed, 09 Aug 1995 00:00:00");
Date.parse("Thu, 01 Jan 1970 00:00:00 GMT");
Date.parse("Thu, 01 Jan 1970 00:00:00");
Date.parse("Thu, 01 Jan 1970 00:00:00 GMT-0400");
`}</code></pre>
    <h2 {...{
      "id": "date---string"
    }}>{`Date -> String`}</h2>
    <p>{`Have a date and want a string? You're shit out of luck son.`}</p>
    <p>{`Pretty much the only date to string conversions javascript natively supports is returning a locale string, which is nonstandard and varies greatly computer to computer, and forms of standardized `}<a parentName="p" {...{
        "href": "http://en.wikipedia.org/wiki/ISO_8601",
        "title": "ISO 8601"
      }}>{`ISO time`}</a>{`. It can do this and only this in numerous ways even!`}</p>
    <pre><code parentName="pre" {...{
        "className": "language-javascript"
      }}>{`> var d = new Date()
> d
Mon, 12 Dec 2011 11:28:55 GMT
> d.toString()
'Mon Dec 12 2011 12:28:55 GMT+0100 (CET)'
> d.toDateString()
'Mon Dec 12 2011'
> d.toISOString()
'2011-12-12T11:28:55.401Z'
> d.toJSON()
'2011-12-12T11:28:55.401Z'
> d.toGMTString()
'Mon, 12 Dec 2011 11:28:55 GMT'
> d.toLocaleDateString()
'Monday, December 12, 2011'
> d.toLocaleString()
'Mon Dec 12 2011 12:28:55 GMT+0100 (CET)'
> d.toLocaleTimeString()
'12:28:55'
> d.toString()
'Mon Dec 12 2011 12:28:55 GMT+0100 (CET)'
> d.toTimeString()
'12:28:55 GMT+0100 (CET)'
> d.toUTCString()
'Mon, 12 Dec 2011 11:28:55 GMT'
`}</code></pre>
    <p>{`Notice how none of those functions accept a format parameter? Sure, you could get those strings and hack them apart to create a string formatted to your liking ... but I'm not sure that's the ideal approach.`}</p>
    <p>{`And let's not even get into wanting to coerce a 12-hour time format out of this thing. Perhaps if your computer is situated in the US the localeString would return that?`}</p>
    <p>{`However I was able to find a jquery plugin that implements `}<a parentName="p" {...{
        "href": "http://projects.nocternity.net/index.py/en/jquery-strftime"
      }}>{`a rough strftime function`}</a>{`, although it doesn't seem to have been actively worked on for the past three years and there are many things missing.`}</p>
    <h2 {...{
      "id": "what"
    }}>{`What?`}</h2>
    <p>{`So there you have it. JavaScript's epic and yet abysmally poor support for date->string conversions.`}</p>
    <p>{`What do you think is it about the javascript ecosystem that tolerates this? Countless developers must have been implementing shitty code because of this and nobody has thought to complain? Nobody has gone so far as to suggest adding a simple strftime function to the language? Most popular languages seem to have this in their standard library ...`}</p>
    <h6 {...{
      "id": "related-articles"
    }}>{`Related articles`}</h6>
    <ul>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8472154/javascript-how-to-get-the-name-of-the-key-inside-a-function"
        }}>{`JavaScript - How to get the name of the key inside a function`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/8470712/javascript-random-color-function-control-brightness-and-contrast"
        }}>{`Javascript - Random Color Function (control brightness and contrast)`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://stackoverflow.com/questions/7728251/string-containing-the-literal-of-a-string-in-javascript"
        }}>{`String containing the literal of a string in JavaScript`}</a>{` (stackoverflow.com)`}</li>
      <li parentName="ul"><a parentName="li" {...{
          "href": "http://marketing.yell.com/web-design/javascript-brief-introduction/"
        }}>{`Javascript: A very brief introduction`}</a>{` (marketing.yell.com)`}</li>
    </ul>
    <p><a parentName="p" {...{
        "href": "http://www.zemanta.com/",
        "title": "Enhanced by Zemanta"
      }}><img parentName="a" {...{
          "src": "https://img.zemanta.com/zemified_e.png?x-id=2aa80621-36dc-42f1-9d5a-f303dc5fe62b",
          "alt": "Enhanced by Zemanta"
        }}></img></a></p>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      